import { type SerializedError, createAsyncThunk, createEntityAdapter, createSlice } from '@reduxjs/toolkit';
import { httpClientInterface } from 'app-components/utils/core';
import { getServiceUrl } from 'app-components/utils/core/interfaces/httpClientInterface';

import RootState from 'app-components/utils/redux-store/rootState';
import { DOMAIN_LOGOS_REDUCER_KEY, type DomainLogoProps, DomainLogosItemsState } from './domainLogosTypes';

const domainLogosAdapter = createEntityAdapter<DomainLogoProps, string>({
  selectId: (logo: DomainLogoProps) => logo.domain,
});

const initialState: DomainLogosItemsState = domainLogosAdapter.getInitialState({
  entities: {},
  ids: [],
  isLoading: false,
  loadPending: true,
  error: null,
  lastSyncDate: null,
});

export const fetchDomainLogos = createAsyncThunk('DOMAIN_LOGOS', async (domains: string[], { rejectWithValue }) => {
  try {
    const url = `${getServiceUrl()}/logos/domains`;
    const response = await httpClientInterface.send('POST', url, { domains });
    const data = response.data as { resources: DomainLogoProps[] };
    return data.resources;
  } catch (error) {
    return rejectWithValue(error || 'Unexpected error');
  }
});

const selectDomainLogosState = (state: RootState) => state[DOMAIN_LOGOS_REDUCER_KEY];

export const domainLogosSelectors = domainLogosAdapter.getSelectors(selectDomainLogosState);

const domainLogosSlice = createSlice({
  name: DOMAIN_LOGOS_REDUCER_KEY,
  initialState,
  reducers: {},
  extraReducers: (builder) => {
    builder
      .addCase(fetchDomainLogos.pending, (state) => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(fetchDomainLogos.fulfilled, (state, action) => {
        domainLogosAdapter.upsertMany(state, action.payload);
        state.isLoading = false;
      })
      .addCase(fetchDomainLogos.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.error.message as SerializedError;
      });
  },
});

export const domainLogosActions = domainLogosSlice.actions;
export { domainLogosSlice };
